import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Snackbar,
  LinearProgress
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

export interface NotificationDialogProps {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
  onClose: () => void;
  autoHideDuration?: number;
}

const NotificationDialog: React.FC<NotificationDialogProps> = ({
  open,
  message,
  severity,
  onClose,
  autoHideDuration = 5000
}) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (open) {
      setProgress(100);
      const timer = setInterval(() => {
        setProgress((prevProgress) => {
          const newProgress = prevProgress - (100 / (autoHideDuration / 100));
          if (newProgress <= 0) {
            clearInterval(timer);
            onClose();
            return 0;
          }
          return newProgress;
        });
      }, 100);

      return () => {
        clearInterval(timer);
      };
    }
  }, [open, onClose, autoHideDuration]);

  const getBackgroundColor = () => {
    switch (severity) {
      case 'success':
        return '#2e7d32';
      case 'error':
        return '#d32f2f';
      case 'warning':
        return '#ed6c02';
      case 'info':
        return '#0288d1';
      default:
        return '#2e7d32';
    }
  };

  const getIcon = () => {
    switch (severity) {
      case 'success':
        return <CheckIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return <CheckIcon />;
    }
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ mt: 8 }}
    >
      <Paper
        sx={{
          p: 2,
          backgroundColor: getBackgroundColor(),
          color: 'white',
          minWidth: '300px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 3px 5px 2px rgba(0, 0, 0, .3)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getIcon()}
            <Typography variant="body1">{message}</Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'white', p: 0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: 'white'
            }
          }}
        />
      </Paper>
    </Snackbar>
  );
};

export default NotificationDialog;
